import React, { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, LinearProgress, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

import useNotification from '../../../../core/hooks/use_notification';
import { createPodNotificationData, getAllIndoorPartnersNotificationData, getAllPriceListsNotificationData, getDevicesNotificationData, getHubsNotificationData, getPodNotificationData, getPodPriceListNotificationData, updatePodNotificationData, updatePodPriceListNotificationData } from '../../../../core/utils/notification_utils';
import { Button, SubmitButton } from '../../../common/button';
import { GET_ROUTE_POD_SALES_DISTRIBUTION, GET_ROUTE_PODS_EDIT, ROUTE_PODS } from '../../../../core/constants';
import TextFieldFormik from '../../../common/text_field';
import { getPodAction, getPodState } from '../../../../features/pod/get_pod';
import { getAllIndoorPartnersAction, getAllIndoorPartnersState } from '../../../../features/pod/get_all_indoor_partners';
import { SelectFieldFormik } from '../../../common/select_field';
import { getAccessTypeOptions, getLocationTypeOptions } from '../../../common/utils/pod_utils';
import { updatePodAction, updatePodState } from '../../../../features/pod/update_pod';
import { getDevicesAction, getDevicesState } from '../../../../features/pod/get_devices';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import { createPodAction, createPodState } from '../../../../features/pod/create_pod';
import RadioGroupFormik from '../../../common/radio_group';
import { yesNoOptions } from '../../../common/form_utils';
import { getAllHubsAction, getAllHubsState } from '../../../../features/pop/get_all_hubs';
import PageHeader from '../../../common/page_header';
import SectionPaper from '../../../common/section_paper';
import BottomActionBar from '../../../common/bottom_action_bar';
import { getAllPriceListsAction, getAllPriceListsState } from '../../../../features/price/get_all_price_lists';
import { getPodPriceListAction, getPodPriceListState } from '../../../../features/price/get_pod_price_list';
import { updatePodPriceListAction, updatePodPriceListState } from '../../../../features/price/update_pod_price_list';
import { deletePodPriceListAction, deletePodPriceListState } from '../../../../features/price/delete_pod_price_list';

const PodForm = () => {
    const { showErrorMessage, showNotification } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { podId } = useParams();
    const location = useLocation();

    const getPod = useSelector(getPodState);
    const getDevices = useSelector(getDevicesState);
    const getAllHubs = useSelector(getAllHubsState);
    const getAllIndoorPartners = useSelector(getAllIndoorPartnersState);
    const getAllPriceLists = useSelector(getAllPriceListsState);
    const getPodPriceList = useSelector(getPodPriceListState);
    const createPod = useSelector(createPodState);
    const updatePod = useSelector(updatePodState);
    const updatePodPriceList = useSelector(updatePodPriceListState);
    const deletePodPriceList = useSelector(deletePodPriceListState);

    const initialValues = {
        name: (podId && getPod?.data?.name) || '',
        deviceId: getDevices?.data?.find(device => device?.id === getPod?.data?.device?.id)?.id || '',
        description: (podId && getPod?.data?.description) || '',
        address: (podId && getPod?.data?.address) || '',
        longitude: (podId && getPod?.data?.longitude) || '',
        latitude: (podId && getPod?.data?.latitude) || '',
        accessType: (podId && getPod?.data?.accessType) || '',
        indoorPartnerId: getAllIndoorPartners?.data?.find(indoorPartner => indoorPartner?.partnerId === getPod?.data?.indoorPartner?.partnerId)?.partnerId || '',
        supportDynamicResources: (podId && getPod?.data?.supportDynamicResources) || false,
        locationType: (podId && getPod?.data?.locationTypeEnum) || '',
        supportedHubId: (podId && getPod?.data?.supportedHubId) || null,
        priceListId: (podId && getPodPriceList?.data?.id) || null,
    };

    const schema = Yup.object().shape({
        name: Yup.string().required(),
        deviceId: Yup.string().nullable(),
        description: Yup.string().required(),
        address: Yup.string().required(),
        longitude: Yup.number().required(),
        latitude: Yup.number().required(),
        accessType: Yup.string().required(),
        indoorPartnerId: Yup.number().required(),
        supportDynamicResources: Yup.bool().required(),
        locationType: Yup.string().required(),
        supportedHubId: Yup.number().nullable(),
        priceListId: Yup.number().nullable(),
    });

    useEffect(() => {
        podId && dispatch(getPodAction({ id: podId }))
            .then(response => showErrorMessage(getPodNotificationData(response)));
        podId && dispatch(getPodPriceListAction({ id: podId }))
            .then(response => showErrorMessage(getPodPriceListNotificationData(response)));

        dispatch(getAllIndoorPartnersAction())
            .then(response => showErrorMessage(getAllIndoorPartnersNotificationData(response)));
        dispatch(getDevicesAction())
            .then(response => showErrorMessage(getDevicesNotificationData(response)));
        dispatch(getAllHubsAction())
            .then(response => showErrorMessage(getHubsNotificationData(response)));
        dispatch(getAllPriceListsAction())
            .then(response => showErrorMessage(getAllPriceListsNotificationData(response)));
    }, [podId, dispatch, showErrorMessage]);

    const onSubmit = (values) => {
        const form = { ...values };

        if (!podId) {
            dispatch(createPodAction(form))
                .then(response => {
                    showNotification(createPodNotificationData(response));
                    const podId = response?.payload?.id;
                    const priceListId = form?.priceListId;

                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && priceListId !== null) {
                        dispatch(updatePodPriceListAction({ podId: podId, priceListId: priceListId }))
                            .then(response => {
                                showNotification(updatePodPriceListNotificationData(response));
                                navigate(GET_ROUTE_PODS_EDIT(podId), { state: { tab: location?.state?.tab } });
                            });
                    }
                });
        } else {
            dispatch(updatePodAction({ id: podId, form }))
                .then(response => {
                    showNotification(updatePodNotificationData(response));
                    const priceListId = form?.priceListId;

                    if (priceListId !== null) {
                        dispatch(updatePodPriceListAction({ podId: podId, priceListId: priceListId }))
                            .then(response => {
                                showNotification(updatePodPriceListNotificationData(response));
                            });
                    }
                    if (priceListId === null) {
                        dispatch(deletePodPriceListAction(podId))
                            .then(response => {
                                showNotification(updatePodNotificationData(response));
                            });
                    }
                });
        }
    };

    const renderView = () => {
        if (getPod?.loading || getAllIndoorPartners?.loading || getDevices?.loading || getAllHubs?.loading || getAllPriceLists?.loading || getPodPriceList?.loading) {
            return (
                <Box mt={ 6 }>
                    <LinearProgress/>
                </Box>
            );
        }

        return (
            <Formik
                enableReinitialize
                initialValues={ initialValues }
                validationSchema={ schema }
                validateOnChange={ false }
                validateOnBlur={ false }
                onSubmit={ onSubmit }>
                { () => (
                    <Box component={ Form }
                         sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' } }>

                        <Box sx={ { flexGrow: 1, mb: 4 } }>
                            <Stack spacing={ 3 }>
                                <SectionPaper title="Partner i Urządzenie">
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="indoorPartnerId"
                                                label="Partner handlowy"
                                                options={ getAllIndoorPartners?.data?.map(indoorPartner => ({
                                                    value: indoorPartner?.partnerId,
                                                    label: indoorPartner?.name
                                                })) }
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="deviceId"
                                                label="Urządzenie"
                                                options={ [{
                                                    value: null,
                                                    label: 'Nie wybrano'
                                                }, ...getDevices?.data?.map(device => ({
                                                    value: device?.id,
                                                    label: device?.serviceId
                                                })) || []] }
                                            />
                                        </Grid>
                                    </Grid>
                                </SectionPaper>

                                <SectionPaper title="Dane Podstawowe i Lokalizacja">
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="name" label="Nazwa" required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="description" label="Opis" required/>
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <TextFieldFormik name="address" label="Adres" required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="longitude" label="Długość geograficzna" required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <TextFieldFormik name="latitude" label="Szerokość geograficzna" required/>
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="locationType"
                                                label="Typ lokalizacji"
                                                options={ getLocationTypeOptions }
                                                required
                                            />
                                        </Grid>
                                    </Grid>
                                </SectionPaper>

                                <SectionPaper title="Konfiguracja i Dostęp">
                                    <Grid container spacing={ 2 }>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="accessType"
                                                label="Typ dostępu"
                                                options={ getAccessTypeOptions }
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="supportedHubId"
                                                label="Umożliwiaj zamówienia w ramach hub"
                                                options={ [{
                                                    value: null,
                                                    label: 'Nie wybrano'
                                                }, ...getAllHubs?.data?.map(hub => ({
                                                    value: hub?.id,
                                                    label: hub?.name
                                                })) || []] }
                                            />
                                        </Grid>
                                        <Grid item xs={ 12 }>
                                            <Stack direction="row" sx={ { alignItems: 'center' } }>
                                                <RadioGroupFormik
                                                    name="supportDynamicResources"
                                                    label="Wspiera dynamiczne dodawanie produktów (PW)"
                                                    options={ yesNoOptions }
                                                    required
                                                />
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </SectionPaper>

                                <SectionPaper title="Cennik">
                                    <Grid container>
                                        <Grid item xs={ 12 } sm={ 6 }>
                                            <SelectFieldFormik
                                                name="priceListId"
                                                label="Cennik"
                                                options={ [{
                                                    value: null,
                                                    label: 'Brak'
                                                }, ...getAllPriceLists?.data?.map(priceList => ({
                                                    value: priceList?.id,
                                                    label: priceList?.name
                                                })) || []] }
                                            />
                                        </Grid>
                                    </Grid>
                                </SectionPaper>
                            </Stack>
                        </Box>

                        <BottomActionBar>
                            <SubmitButton isLoading={ createPod?.loading || updatePod?.loading || updatePodPriceList?.loading || deletePodPriceList?.loading }>
                                Zapisz
                            </SubmitButton>
                        </BottomActionBar>

                    </Box>
                ) }
            </Formik>
        );
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>
            <PageHeader
                title={ podId ? getPod?.data?.description || 'Edycja punktu sprzedaży' : 'Nowy punkt sprzedaży' }
                showBack={ true }
                onBack={ () => navigate(ROUTE_PODS, { state: { tab: location?.state?.tab } }) }
                titleAdornment={
                    podId && getPod?.data && (
                        <Typography variant="h6" color="textSecondary">
                            ID: { podId }
                        </Typography>
                    )
                }
                actions={
                    podId && (
                        <Button startIcon={ <QueryStatsIcon/> } onClick={ () => navigate(GET_ROUTE_POD_SALES_DISTRIBUTION(podId)) }>
                            Rozkład sprzedaży
                        </Button>
                    )
                }
            />
            { renderView() }
        </Box>
    );
};

export default PodForm;